// Port of prototype/components/hud/BeeTypeBar.jsx (design D3), extended for the sandbox
// screen's bee-type bar (`client-sandbox-screen` task 5.4, design §3.4, visual spec §4).
// UI.md §4: the bee-type dock — one BeeTypeButton per player-defined type (sprite, count,
// type-colour underline, optional alert pip) plus a dashed "+" to create a new type. Every tap
// target meets the ≥44 px hit target (`--hit-target`); the chips are 60 × 48 and the create
// button 46 × 48 (visual spec §4.1/§4.2), so both clear it on both axes.
//
// Two additions the sandbox screen needs, both of them rules rather than decoration:
//
//   * **A count may be absent, and absence is the placeholder.** `count` is now `number | null`.
//     The sandbox binds each chip to `ui/rounds/type-counts.ts` at the playhead, and that model
//     answers `null` when the playhead has no resident record — a range still in flight, or the
//     coarse-only tier, which carries no per-type breakdown at all (gap **G-A**). The chip then
//     renders the em dash. It must not render the number it was showing a moment ago: a stale
//     composition is indistinguishable from a live one, and reading the composition is the
//     reason the bar exists. A real `0` is *not* the placeholder — a type that has died out is
//     exactly the collapse the player is hunting, and `type-counts.ts` keeps the two apart.
//   * **The create control is disabled at the type cap, with the cap stated.** A control that
//     silently stops working teaches nothing; the 12-type cap is a rule of the game (DESIGN
//     §14, ≤ 12 types per colony) rather than a failure the player has caused, so it is said
//     out loud on the control itself.
//
// `abbrev` is optional so live mode's dock keeps exactly the DOM it has today: the sandbox's
// chips carry the uppercase type abbreviation under the count (visual spec §4.1), and a caller
// that supplies none simply gets the sprite/count stack.
//
// The alert dot carries a `data-testid` (not present in the kit markup) so the client can assert
// its presence without depending on visual-only styling — a test hook, not a design change.

import type { CSSProperties } from 'react';
import { COUNTER_PLACEHOLDER } from './ResourceCounter';

/** Maximum bee types per colony (DESIGN §14; BPP-SPEC's compile-checked colony limit). */
export const TYPE_CAP = 12;

/**
 * What the create control says once the roster is full. Phrased as the rule it is — a colony
 * may hold this many types — rather than as an error, because the player has done nothing wrong.
 */
export function typeCapNotice(cap: number): string {
  return `Type limit reached — a colony can hold ${cap} bee types`;
}

export interface BeeTypeButtonProps {
  name: string;
  /**
   * The type's living count at the playhead, or `null` where the playhead has no source for it
   * — rendered as {@link COUNTER_PLACEHOLDER}, never as a retained or invented number.
   */
  count: number | null;
  /** Bee-type color token. */
  color: string;
  sprite: string;
  /** Uppercase short label under the count (`HARV`, `NURSE`, …). Omitted in live mode's dock. */
  abbrev?: string;
  selected?: boolean;
  /** Red dot — e.g. compile error or heavy losses. */
  alert?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  /** Overrides the default `bee-type-chip-<name>` test id. The Match screen's side panels render
   * these chips for two colonies at once, so their ids carry the colony as well as the type. */
  testId?: string;
}

export function BeeTypeButton({
  name,
  count,
  color,
  sprite,
  abbrev,
  selected,
  alert,
  onClick,
  style,
  testId,
}: BeeTypeButtonProps) {
  const unavailable = count === null || !Number.isFinite(count);
  return (
    <button
      data-testid={testId ?? `bee-type-chip-${name}`}
      aria-pressed={selected === true}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        flex: 'none',
        width: 60,
        height: 48,
        minHeight: 'var(--hit-target)',
        minWidth: 'var(--hit-target)',
        // Clears the type-colour underline pinned to the bottom edge (visual spec §4.1).
        paddingBottom: 7,
        position: 'relative',
        borderRadius: 'var(--r-md)',
        cursor: 'pointer',
        background: selected ? 'var(--honey-200)' : 'transparent',
        border: `1.5px solid ${selected ? 'var(--line-strong)' : 'transparent'}`,
        transition: 'all var(--dur-fast)',
        ...style,
      }}
    >
      {alert && (
        <span
          data-testid="bee-type-alert"
          style={{
            position: 'absolute',
            top: 2,
            right: 8,
            width: 9,
            height: 9,
            borderRadius: 5,
            background: 'var(--danger)',
            border: '1.5px solid var(--hud-bg)',
          }}
        />
      )}
      <img
        src={sprite}
        alt={name}
        style={{ width: 17, height: 17, imageRendering: 'pixelated', objectFit: 'contain' }}
      />
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 13,
          // Dimmed when there is nothing to report, so the chip reads as "no source" rather
          // than as a live reading that happens to look odd.
          color: unavailable ? 'var(--hud-text-dim)' : 'var(--hud-text)',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.05,
        }}
      >
        {unavailable ? COUNTER_PLACEHOLDER : count}
      </span>
      {abbrev && (
        <span
          style={{
            fontSize: 7,
            fontWeight: 800,
            color: 'var(--hud-text-dim)',
            lineHeight: 1,
            letterSpacing: '0.04em',
          }}
        >
          {abbrev}
        </span>
      )}
      <span
        style={{
          position: 'absolute',
          left: 13,
          right: 13,
          bottom: 4,
          height: 3,
          borderRadius: 2,
          background: color,
        }}
      />
    </button>
  );
}

export interface BeeTypeBarProps {
  /** One entry per player-defined type. */
  types: {
    name: string;
    count: number | null;
    color: string;
    sprite: string;
    abbrev?: string;
    alert?: boolean;
  }[];
  selectedName?: string;
  onSelect?: (name: string) => void;
  /** Shows the dashed "+" new-type button. */
  onAdd?: () => void;
  /** Types a colony may hold; the "+" is disabled and states the cap once `types` reaches it. */
  typeCap?: number;
  style?: CSSProperties;
}

export function BeeTypeBar({
  types,
  selectedName,
  onSelect,
  onAdd,
  typeCap = TYPE_CAP,
  style,
}: BeeTypeBarProps) {
  const atCap = types.length >= typeCap;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: '6px 8px',
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--hud-line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-hud)',
        // Chips never wrap and never shrink; the row scrolls instead (visual spec §4).
        overflowX: 'auto',
        ...style,
      }}
    >
      {types.map((t) => (
        <BeeTypeButton
          key={t.name}
          {...t}
          selected={t.name === selectedName}
          onClick={() => onSelect?.(t.name)}
        />
      ))}
      {onAdd && (
        <button
          data-testid="bee-type-add"
          onClick={onAdd}
          disabled={atCap}
          title={atCap ? typeCapNotice(typeCap) : 'New bee type'}
          aria-label="New bee type"
          style={{
            width: 46,
            height: 48,
            minHeight: 'var(--hit-target)',
            minWidth: 'var(--hit-target)',
            borderRadius: 'var(--r-md)',
            flex: 'none',
            cursor: atCap ? 'default' : 'pointer',
            opacity: atCap ? 0.38 : 1,
            border: '1.5px dashed var(--hud-line)',
            background: 'transparent',
            color: 'var(--hud-text-dim)',
            fontSize: 20,
            fontFamily: 'var(--font-display)',
          }}
        >
          +
        </button>
      )}
    </div>
  );
}
